// @ts-check
// Escolha de um modelo aprovado do WhatsApp com as variáveis e a prévia (contrato 08 de leads e WhatsApp, seção 7):
// usada no envio de modelo numa conversa com a janela de 24 horas fechada e na Nova conversa, para falar primeiro com
// quem nunca escreveu. Desde a 2.0.0 (contrato 09 de campanhas de WhatsApp, seção 2) os modelos vêm em grupos, com os
// de atendimento primeiro, depois comunicados e marketing, dentro do mesmo listbox. Os de marketing ficam desligados,
// com o motivo, quando a pessoa da conversa não pode receber marketing pelo WhatsApp ou a conversa não tem pessoa (frente
// 2B, spec/whatsapp.md 3.7.E); o 422 MARKETING_CONSENT_REQUIRED do envio continua como garantia.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { listWhatsappTemplates, renderTemplateText } from '../../data/integrations.js';
import { fetchMarketingStatus } from '../../data/marketing-consent.js';
import { Loader } from '../../ui/feedback.js';
import { templateVariables } from '../../lib/whatsapp-templates.js';
import { templateGroupsFor } from '../../../../shared/whatsapp/template-rules.js';
import { hasTranslation, t } from '../../i18n/index.js';

/** @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate */

/**
 * Modelos aprovados que o CRM consegue enviar (sem cabeçalho de mídia); nulo enquanto carrega, vazio na falha.
 * @param {string | null} [channelId] só os do número; sem ele, os de todos os números
 */
export function useSendableTemplates(channelId = null) {
  const [templates, setTemplates] = useState(/** @type {WhatsappTemplate[] | null} */ (null));
  useEffect(() => {
    let isCurrent = true;
    setTemplates(null);
    listWhatsappTemplates({ channelId, status: 'APPROVED' })
      .then((list) => {
        if (isCurrent) setTemplates(list.filter((template) => template.isSendable));
      })
      .catch(() => {
        if (isCurrent) setTemplates([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [channelId]);
  return templates;
}

/**
 * Bloqueio dos modelos de marketing na conversa: sem pessoa, "vincule a uma pessoa"; com pessoa que não pode receber
 * marketing pelo WhatsApp, o motivo da base. Lido uma vez ao abrir; nulo enquanto lê ou quando pode receber.
 * @param {string | null | undefined} personId @param {boolean} [enabled]
 * @returns {{ reason: string } | null}
 */
export function useMarketingBlock(personId, enabled = true) {
  const [block, setBlock] = useState(/** @type {{ reason: string } | null} */ (null));
  useEffect(() => {
    let isCurrent = true;
    if (!enabled) {
      setBlock(null);
      return undefined;
    }
    if (!personId) {
      setBlock({ reason: 'NO_PERSON' });
      return undefined;
    }
    setBlock(null);
    fetchMarketingStatus(personId)
      .then((status) => {
        const whatsapp = status.channels?.WHATSAPP;
        if (isCurrent) setBlock(whatsapp && whatsapp.isEligible === false ? { reason: whatsapp.reason ?? 'NO_CONSENT' } : null);
      })
      .catch(() => {
        // sem a leitura, o envio decide (422 MARKETING_CONSENT_REQUIRED)
        if (isCurrent) setBlock(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [personId, enabled]);
  return block;
}

/** Texto do bloqueio de marketing. @param {{ reason: string }} block */
export function marketingBlockText(block) {
  if (block.reason === 'NO_PERSON') return t('whatsappTemplates.picker.marketingNeedsPerson');
  const key = `whatsappTemplates.picker.marketingBlocked.${block.reason}`;
  return hasTranslation(key) ? t(key) : t('whatsappTemplates.picker.marketingBlocked.OTHER');
}

/**
 * @param {{ templates: WhatsappTemplate[] | null, selectedId: string, onSelect: (templateId: string) => void, values: Record<string, string>,
 *   onValuesChange: (values: Record<string, string>) => void, emptyText?: string, marketingBlock?: { reason: string } | null }} props
 */
export function TemplatePicker({ templates, selectedId, onSelect, values, onValuesChange, emptyText, marketingBlock = null }) {
  const selected = templates?.find((template) => template.id === selectedId) ?? null;
  return html`${templates === null
    ? html`<${Loader} />`
    : templates.length === 0
      ? html`<p class="integration-empty">${emptyText ?? t('leadCapture.templateMessage.empty')}</p>`
      : html`<div class="template-picker" role="listbox" aria-label=${t('leadCapture.templateMessage.choose')}>
          ${templateGroupsFor(templates, 'CONVERSATION').filter((group) => group.templates.length > 0).map((group) => html`
            <div key=${`group-${group.key}`} class="template-picker-group" role="presentation" data-group=${group.key}>${t(`whatsappTemplates.groups.${group.key}`)}</div>
            ${group.templates.map((template) => {
              const isBlocked = Boolean(marketingBlock) && template.category === 'MARKETING';
              return html`<button key=${template.id} type="button" role="option" aria-selected=${template.id === selectedId ? 'true' : 'false'}
                aria-disabled=${isBlocked ? 'true' : undefined} class="template-option" data-selected=${template.id === selectedId ? 'true' : undefined}
                data-blocked=${isBlocked ? 'true' : undefined} onClick=${() => {
                  if (!isBlocked) onSelect(template.id);
                }}>
                <span class="template-item-name">${template.name}</span>
                <span class="template-option-body">${template.bodyText}</span>
                ${isBlocked && marketingBlock ? html`<span class="template-option-blocked">${marketingBlockText(marketingBlock)}</span>` : null}
              </button>`;
            })}`)}
        </div>`}
    ${selected
      ? html`
          ${templateVariables(selected).map((variable) => html`<label key=${variable} class="form-field">
            <span class="form-field-label">${t('leadCapture.templateMessage.variable', { variable })}</span>
            <input class="text-input" value=${values[variable] ?? ''} maxLength=${200}
              onInput=${(/** @type {Event} */ event) => onValuesChange({ ...values, [variable]: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
          </label>`)}
          <div class="template-preview" aria-label=${t('leadCapture.templateMessage.preview')}>
            <span class="form-field-label">${t('leadCapture.templateMessage.preview')}</span>
            <div class="template-preview-bubble">
              ${selected.headerText ? html`<strong>${renderTemplateText(selected.headerText, values)}</strong>` : null}
              <span>${renderTemplateText(selected.bodyText, values)}</span>
              ${selected.footerText ? html`<small>${selected.footerText}</small>` : null}
            </div>
          </div>`
      : null}`;
}
